/**
 * segmentEvaluator — wertet Arbeitspaket-Regeln clientseitig auf der voll
 * geladenen Arbeitsliste aus (P2b/G1). Pure Function, keine Requests.
 * listed_on_channel mappt Channel-UUID → JTL-Id (active_channels trägt JTL-Ids).
 */
import type { ProductSegmentRule, SegmentCondition } from './segmentPresets'

type ChannelRef = { id: string; channel_id: string | null }
type Row = {
  revenue_30d?: number | null
  quantity_30d?: number | null
  issue_count: number
  active_channels?: string[]
  channel_sales?: Record<string, { quantity_30d: number; revenue_30d: number }>
}

const num = (v: number | null | undefined) => v ?? 0

function channelMetric(row: Row, c: SegmentCondition): number {
  const sales = c.channel_id ? row.channel_sales?.[c.channel_id] : undefined
  return c.field === 'channel_quantity_30d' ? num(sales?.quantity_30d) : num(sales?.revenue_30d)
}

/** JTL-Ids vergleichbar machen (#578). `raw_data.ActiveSalesChannels` traegt an
 *  dritter Stelle konstant den Platzhalter "-1" ("11-8--1-18"), `channels.channel_id`
 *  dort die echte Kanalnummer ("11-8-8-18"). Blosses Zusammenziehen der Bindestriche
 *  trifft deshalb nur dort, wo die echte Nummer zufaellig 1 ist (kaufland) — otto.de
 *  und der Amazon-Lister galten dadurch immer als "nicht gelistet".
 *  Verlaesslich sind Plattform, Instanz und die LETZTE Komponente; an allen 13
 *  prod-Kanaelen geprueft (2026-08-12), kollisionsfrei auch fuer die Paare
 *  5-30-0/5-30-1 und 1-1-0/1-1-1. */
const jtlKey = (s: string) => {
  const parts = s.split('-').filter((p) => p !== '')
  if (parts.length < 3) return parts.join('-')
  return `${parts[0]}-${parts[1]}-${parts[parts.length - 1]}`
}

function matches(row: Row, c: SegmentCondition, jtlByUuid: Map<string, string>): boolean {
  switch (c.field) {
    case 'channel_quantity_30d':
    case 'channel_revenue_30d': {
      const v = channelMetric(row, c)
      return c.op === 'lte' ? v <= Number(c.value) : v >= Number(c.value)
    }
    case 'listed_on_channel': {
      const jtl = c.channel_id ? jtlByUuid.get(c.channel_id) : undefined
      const listed = !!jtl && (row.active_channels ?? []).some((a) => jtlKey(a) === jtlKey(jtl))
      return listed === Boolean(c.value)
    }
    case 'has_issues':
      return (row.issue_count > 0) === Boolean(c.value)
    // Klassische Products-Segment-Felder REAL auswerten (Review-Befund: die Daten
    // liegen seit P2a auf der Arbeitsliste — „neutral" wäre ein irreführender Zähler):
    case 'quantity_30d_min':
      return num(row.quantity_30d) >= Number(c.value)
    case 'revenue_30d_min':
      return num(row.revenue_30d) >= Number(c.value)
    case 'quantity_90d_min':
      return num((row as { quantity_90d?: number | null }).quantity_90d) >= Number(c.value)
    case 'revenue_90d_min':
      return num((row as { revenue_90d?: number | null }).revenue_90d) >= Number(c.value)
    case 'has_image':
      return (!!(row as { thumbnail_url?: string | null }).thumbnail_url) === Boolean(c.value)
    default:
      return true // nur is_active hat auf der Arbeitsliste keine Datenbasis → neutral (dokumentiert)
  }
}

export function evaluateSegment<T extends Row>(
  rule: ProductSegmentRule,
  rows: T[],
  channels: ChannelRef[],
): T[] {
  const jtlByUuid = new Map(channels.filter((c) => c.channel_id).map((c) => [c.id, c.channel_id as string]))
  const test = (row: T) =>
    rule.match === 'any'
      ? rule.conditions.some((c) => matches(row, c, jtlByUuid))
      : rule.conditions.every((c) => matches(row, c, jtlByUuid))
  let out = rule.conditions.length ? rows.filter(test) : [...rows]
  if (rule.top) {
    const { metric, n, channel_id } = rule.top
    const val = (r: T) => (channel_id ? num(r.channel_sales?.[channel_id]?.[metric === 'revenue_30d' ? 'revenue_30d' : 'quantity_30d']) : num(r[metric] as number | undefined))
    out = [...out].sort((a, b) => val(b) - val(a)).slice(0, Math.max(1, n))
  }
  return out
}
